import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Edit, Image as ImageIcon, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';

type ServiceEntry = {
  id: string;
  car_id: string;
  service_date: string;
  shop_name: string | null;
  mechanic_name: string | null;
  location: string | null;
  contact_phone: string | null;
  work_done: Array<{ title: string; notes?: string; spec_code?: string; label_photo?: string; color?: string }> | null;
  findings: string | null;
  recommendations: string | null; // Text field for recommendations
  suggested_next_fixes: Array<{ fix: string; due?: string }> | null; // Array for suggested fixes
  total_cents: number | null;
  status: 'draft' | 'submitted';
  media: Array<{ path: string; type: string }> | null;
  issues_resolved_ids: string[] | null;
  tasks_resolved_ids: string[] | null;
  service_tasks_resolved: Array<{ source_service_entry_id: string; task_text: string }> | null;
  expense_ids: string[] | null; // Array of expense IDs linked to this service entry
  service_request_id: string | null; // Linked service request
  created_at: string;
};

type ResolvedIssue = {
  id: string;
  title: string;
  status: string;
};

type ResolvedTask = {
  id: string;
  title: string;
  status: string;
};

const ServiceEntryViewPage: React.FC = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = searchParams.get('tab') || 'repairs';
  const carId = searchParams.get('carId');
  const { formatCurrency, t } = useI18n();
  const { toast } = useToast();
  const [entry, setEntry] = useState<ServiceEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resolvedIssues, setResolvedIssues] = useState<ResolvedIssue[]>([]);
  const [resolvedTasks, setResolvedTasks] = useState<ResolvedTask[]>([]);
  const [serviceRequest, setServiceRequest] = useState<{ id: string; title: string | null; owner_notes: string | null } | null>(null);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(-1);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const panStartRef = React.useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const loadEntry = async () => {
      if (!entryId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('service_entries')
          .select('*')
          .eq('id', entryId)
          .single();

        if (error) {
          console.error('Failed to load service entry:', error);
          setLoading(false);
          return;
        }

        if (data) {
          setEntry(data as ServiceEntry);

          // Load resolved issues
          if (data.issues_resolved_ids && Array.isArray(data.issues_resolved_ids) && data.issues_resolved_ids.length > 0) {
            try {
              const { data: issuesData, error: issuesError } = await supabase
                .from('symptoms')
                .select('id, title, status')
                .in('id', data.issues_resolved_ids);

              if (!issuesError && issuesData) {
                setResolvedIssues(issuesData as ResolvedIssue[]);
              }
            } catch (e) {
              console.warn('Failed to load resolved issues:', e);
            }
          }

          // Load resolved tasks
          const loadedResolvedTasks: ResolvedTask[] = [];
          if (data.tasks_resolved_ids && Array.isArray(data.tasks_resolved_ids) && data.tasks_resolved_ids.length > 0) {
            try {
              const { data: tasksData, error: tasksError } = await supabase
                .from('tasks')
                .select('id, title, status')
                .in('id', data.tasks_resolved_ids);

              if (!tasksError && tasksData) {
                loadedResolvedTasks.push(...(tasksData as ResolvedTask[]));
              }
            } catch (e) {
              console.warn('Failed to load resolved tasks:', e);
            }
          }

          if (data.service_tasks_resolved && Array.isArray(data.service_tasks_resolved) && data.service_tasks_resolved.length > 0) {
            for (const ref of data.service_tasks_resolved) {
              if (!ref?.task_text) continue;
              loadedResolvedTasks.push({
                id: `svc:${ref.source_service_entry_id}:${ref.task_text}`,
                title: ref.task_text,
                status: 'done',
              });
            }
          }

          setResolvedTasks(loadedResolvedTasks);

          // Load media URLs
          if (data.media && Array.isArray(data.media) && data.media.length > 0) {
            const urls: Record<string, string> = {};
            for (const mediaItem of data.media) {
              if (mediaItem.path) {
                try {
                  const signedUrlInfo = await getSignedViewUrl(mediaItem.path);
                  urls[mediaItem.path] = signedUrlInfo.url;
                } catch (e) {
                  console.warn('Failed to get signed URL for', mediaItem.path, e);
                }
              }
            }
            setMediaUrls(urls);
          }

          // Load service request if linked
          if (data.service_request_id) {
            try {
              const { data: requestData, error: requestError } = await supabase
                .from('service_requests')
                .select('id, title, owner_notes')
                .eq('id', data.service_request_id)
                .single();

              if (!requestError && requestData) {
                setServiceRequest(requestData);
              }
            } catch (e) {
              console.warn('Failed to load service request:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error loading service entry:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEntry();
  }, [entryId]);

  // Compute derived values safely (before early returns)
  const workItems = entry ? (Array.isArray(entry.work_done) ? entry.work_done : []) : [];
  const suggestedFixes = entry ? (Array.isArray(entry.suggested_next_fixes) ? entry.suggested_next_fixes : []) : [];
  const mediaItems = entry ? (Array.isArray(entry.media) ? entry.media : []) : [];

  // Handle ESC key, arrow keys, and zoom for full screen image
  // NOTE: This must be called before any early returns to follow Rules of Hooks
  useEffect(() => {
    if (!fullScreenImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFullScreenImage(null);
        setCurrentImageIndex(-1);
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      } else if (e.key === 'ArrowLeft' && currentImageIndex > 0) {
        const newIndex = currentImageIndex - 1;
        setCurrentImageIndex(newIndex);
        setFullScreenImage(mediaItems[newIndex] ? mediaUrls[mediaItems[newIndex].path] || null : null);
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      } else if (e.key === 'ArrowRight' && currentImageIndex < mediaItems.length - 1) {
        const newIndex = currentImageIndex + 1;
        setCurrentImageIndex(newIndex);
        setFullScreenImage(mediaItems[newIndex] ? mediaUrls[mediaItems[newIndex].path] || null : null);
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setImageZoom(prev => Math.max(1, Math.min(5, prev + delta)));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = 'unset';
    };
  }, [fullScreenImage, currentImageIndex, mediaItems, mediaUrls]);

  const handleBack = () => {
    if (carId) {
      navigate(`/records/${carId}?tab=${tab}`);
    } else {
      navigate(-1);
    }
  };

  const handleDelete = async () => {
    if (!entry) return;

    try {
      // First, delete the linked expense records if they exist
      if (entry.expense_ids && entry.expense_ids.length > 0) {
        const { error: expenseError } = await supabase
          .from('expenses')
          .delete()
          .in('id', entry.expense_ids);

        if (expenseError) {
          console.warn('Failed to delete linked expenses:', expenseError);
          // Continue with service entry deletion even if expense deletion fails
        }
      }

      // Then delete the service entry
      const { error } = await supabase
        .from('service_entries')
        .delete()
        .eq('id', entry.id);

      if (error) throw error;

      toast({
        title: t('common.success') || 'Success',
        description: t('common.deleted') || 'Service entry deleted successfully',
      });

      navigate(-1);
    } catch (error) {
      console.error('Failed to delete service entry:', error);
      toast({
        title: t('common.error') || 'Error',
        description: t('common.deleteFailed') || 'Failed to delete service entry',
        variant: 'destructive',
      });
    }
  };

  // Early returns after all hooks have been called
  if (loading) {
    return (
      <PageLayout title={t('common.loading') || 'Loading...'} showBackButton onBack={handleBack}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading service entry...</div>
        </div>
      </PageLayout>
    );
  }

  if (!entry) {
    return (
      <PageLayout title={t('common.notFound') || 'Not Found'} showBackButton onBack={handleBack}>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <h2 className="text-2xl font-bold mb-2">{t('common.notFound') || 'Service Entry Not Found'}</h2>
          <p className="text-gray-500 mb-6">{t('common.entryNotFound') || 'The service entry you\'re looking for doesn\'t exist'}</p>
          <Button onClick={handleBack} className="bg-primary">
            {t('actions.goBack') || 'Go Back'}
          </Button>
        </div>
      </PageLayout>
    );
  }

  // Calculate totalCost after we know entry exists
  const totalCost = entry.total_cents ? entry.total_cents / 100 : null;

  return (
    <PageLayout
      title=""
      showBackButton
      noBottomPadding
      onBack={() => {
        if (carId) {
          navigate(`/records/${carId}?tab=${tab}`);
        } else {
          navigate(-1);
        }
      }}
      className="bg-[#f5f5f5]"
    >
      <div className="w-full font-lato text-black p-4">
        {/* HEADER */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-lg font-bold">{entry.shop_name || entry.location || t('addService.serviceEntry') || 'Service Entry'}</h1>
              {entry.mechanic_name && (
                <p className="text-sm text-gray-600">{t('addService.mechanicLabel') || 'Mechanic'}: {entry.mechanic_name}</p>
              )}
            </div>
            <span
              className={`text-xs px-2 py-1 rounded-lg border ${
                entry.status === 'submitted'
                  ? 'bg-green-100 text-green-700 border-green-300'
                  : 'bg-gray-100 text-gray-700 border-gray-300'
              }`}
            >
              {entry.status === 'submitted' ? t('common.submitted') || 'Submitted' : t('common.draft') || 'Draft'}
            </span>
          </div>

          <div className="mt-3 flex justify-between text-sm text-gray-700">
            <span>{format(new Date(entry.service_date), 'dd MMM yyyy')}</span>
            {totalCost !== null && (
              <span className="font-semibold">{formatCurrency(totalCost)}</span>
            )}
          </div>
        </div>

        {/* WORK DONE */}
        {workItems.length > 0 && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{t('addService.workDone') || 'Work Done'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4 space-y-4">
              {/* Service Request Owner Notes - Non-editable */}
            {serviceRequest && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {t('serviceRequest.ownerNotes') || 'What I want done'}
                  </h4>
                  <a
                    href={`/service-request/view/${serviceRequest.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/service-request/view/${serviceRequest.id}`);
                    }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    {t('serviceRequest.viewServiceRequest') || 'View Service Request'}
                  </a>
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{serviceRequest.owner_notes}</p>
              </div>
            )}

            <ol className="space-y-3 text-sm list-decimal pl-5">
              {workItems.map((item, idx) => (
                <li key={idx}>
                  <div className="font-medium">{item.title}</div>
                  {item.notes && (
                    <div className="text-gray-600 mt-1">{item.notes}</div>
                  )}
                  {(item.spec_code || item.color) && (
                    <div className="text-xs text-gray-500 mt-1 space-y-1">
                      {item.spec_code && (
                        <div>
                          <span className="font-medium">{t('addService.specCode') || 'Specification/Code'}: </span>
                          {item.spec_code}
                        </div>
                      )}
                      {item.color && (
                        <div>
                          <span className="font-medium">{t('addService.color') || 'Color'}: </span>
                          {item.color}
                        </div>
                      )}
                    </div>
                  )}
                  {item.label_photo && mediaUrls[item.label_photo] && (
                    <div 
                      className="mt-2 cursor-pointer"
                      onClick={() => {
                        if (mediaUrls[item.label_photo!]) {
                          // Find index in mediaItems or set to -1 if it's a label photo
                          setCurrentImageIndex(-1);
                          setFullScreenImage(mediaUrls[item.label_photo!]);
                          setImageZoom(1);
                          setImageOffset({ x: 0, y: 0 });
                        }
                      }}
                    >
                      <img
                        src={mediaUrls[item.label_photo]}
                        alt={t('addService.labelPhoto') || 'Label photo'}
                        className="max-w-full h-auto rounded-lg border border-gray-200"
                        style={{ maxHeight: '200px' }}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ol>
            </div>
          </section>
        )}

        {/* ISSUES RESOLVED */}
        {resolvedIssues.length > 0 && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{t('addService.resolvedIssues') || 'Issues Resolved'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="space-y-3 text-sm">
                {resolvedIssues.map((issue) => (
                  <div key={issue.id} className="space-y-1">
                    <div>
                      {issue.status === 'resolved' ? '✔️' : '✖️'} {issue.title}
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => navigate(`/symptom-summary/${carId || entry.car_id}/${issue.id}?tab=issues&carId=${carId || entry.car_id}`)}
                        className="text-[#7c9ffa] hover:underline font-medium text-xs"
                      >
                        View issue
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* TASKS RESOLVED */}
        {resolvedTasks.length > 0 && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{t('addService.resolvedTasks') || 'Tasks Resolved'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="space-y-3 text-sm">
                {resolvedTasks.map((task) => (
                  <div key={task.id}>
                    {(task.status === 'done' || task.status === 'completed') ? '✔️' : '✖️'} {task.title}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FINDINGS */}
        {entry.findings && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{t('addService.findingsTitle') || 'Findings'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.findings}</p>
            </div>
          </section>
        )}

        {/* RECOMMENDATIONS */}
        {entry.recommendations && entry.recommendations.trim() && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{t('addService.recommendationsTitle') || 'Recommendations'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.recommendations}</p>
            </div>
          </section>
        )}

        {/* SUGGESTED NEXT FIXES */}
        {suggestedFixes.length > 0 && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{t('addService.suggestedFixes') || 'Suggested Next Fixes'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <ol className="space-y-3 text-sm list-decimal pl-5">
                {suggestedFixes.map((rec, idx) => (
                  <li key={idx}>
                    <div className="font-medium">{rec.fix}</div>
                    {rec.due && (
                      <div className="text-gray-600 text-xs mt-1">{rec.due}</div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {/* PHOTOS */}
        {mediaItems.length > 0 && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">
              {t('addService.photos') || 'Photos'} ({mediaItems.length})
            </h2>
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="grid grid-cols-2 gap-3">
              {mediaItems.map((mediaItem, idx) => (
                <div 
                  key={idx} 
                  className="relative cursor-pointer"
                  onClick={() => {
                    if (mediaUrls[mediaItem.path]) {
                      setCurrentImageIndex(idx);
                      setFullScreenImage(mediaUrls[mediaItem.path]);
                      setImageZoom(1);
                      setImageOffset({ x: 0, y: 0 });
                    }
                  }}
                >
                  {mediaUrls[mediaItem.path] ? (
                    <img
                      src={mediaUrls[mediaItem.path]}
                      alt={`Photo ${idx + 1}`}
                      className="w-full aspect-square rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-gray-200 rounded-lg" />
                  )}
                </div>
              ))}
              </div>
            </div>
          </section>
        )}

        {/* REPAIR SHOP CARD */}
        {(entry.shop_name || entry.mechanic_name || entry.location || entry.contact_phone) && (
          <section className="mt-4 mb-4">
            <h2 className="text-lg font-semibold mb-2">{t('addService.repairShop') || 'Repair Shop'}</h2>
            <div className="bg-white rounded-xl shadow-md p-4">
            {entry.shop_name && (
              <div className="font-semibold text-base mb-1">
                {entry.shop_name}
              </div>
            )}
            {entry.mechanic_name && (
              <div className="text-sm text-gray-700 mt-1">
                {entry.mechanic_name}
              </div>
            )}
            {entry.location && (
              <div className="text-sm mt-1">
                {entry.location}
              </div>
            )}
            {entry.contact_phone && (
              <div className="text-sm mt-1">
                {entry.contact_phone}
              </div>
            )}
            </div>
          </section>
        )}

        {/* ACTION BUTTONS */}
        <div className="mt-4 mb-4 pb-4 flex gap-2">
          <Button
            variant="destructive"
            className="flex-1 py-3 rounded-xl text-sm font-medium"
            onClick={() => setDeleteDialogOpen(true)}
            type="button"
          >
            {t('actions.delete') || 'Delete'}
          </Button>
          <Button
            variant="secondary"
            className="flex-1 py-3 rounded-xl text-sm font-medium"
            onClick={() => {
              if (carId) {
                navigate(`/records/${carId}?tab=${tab}`);
              } else {
                navigate(-1);
              }
            }}
            type="button"
          >
            {t('actions.close') || 'Close'}
          </Button>
          <Button
            className="flex-1 py-3 rounded-xl bg-[#e7fe52] text-gray-900 text-sm font-medium hover:bg-[#e7fe52]/90"
            onClick={() => navigate(`/services/add?edit=${entry.id}&carId=${entry.car_id}`)}
            type="button"
          >
            {t('actions.edit') || 'Edit'}
          </Button>
        </div>
      </div>

      {/* Full Screen Image Modal */}
      {fullScreenImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => {
            if (imageZoom === 1) {
              setFullScreenImage(null);
              setCurrentImageIndex(-1);
              setImageZoom(1);
              setImageOffset({ x: 0, y: 0 });
            }
          }}
        >
          <button
            onClick={() => {
              setFullScreenImage(null);
              setCurrentImageIndex(-1);
              setImageZoom(1);
              setImageOffset({ x: 0, y: 0 });
            }}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous Button */}
          {mediaItems.length > 1 && currentImageIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = currentImageIndex - 1;
                setCurrentImageIndex(newIndex);
                setFullScreenImage(mediaItems[newIndex] ? mediaUrls[mediaItems[newIndex].path] || null : null);
                setImageZoom(1);
                setImageOffset({ x: 0, y: 0 });
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Next Button */}
          {mediaItems.length > 1 && currentImageIndex >= 0 && currentImageIndex < mediaItems.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = currentImageIndex + 1;
                setCurrentImageIndex(newIndex);
                setFullScreenImage(mediaItems[newIndex] ? mediaUrls[mediaItems[newIndex].path] || null : null);
                setImageZoom(1);
                setImageOffset({ x: 0, y: 0 });
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Image Counter */}
          {mediaItems.length > 1 && currentImageIndex >= 0 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full z-10">
              {currentImageIndex + 1} / {mediaItems.length}
            </div>
          )}

          {/* Image Container with Zoom and Pan */}
          <div
            className="relative w-full h-full flex items-center justify-center touch-none"
            onWheel={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                setImageZoom(prev => Math.max(1, Math.min(5, prev + delta)));
              }
            }}
            onMouseDown={(e) => {
              if (imageZoom > 1 && e.button === 0) {
                panStartRef.current = { x: e.clientX - imageOffset.x, y: e.clientY - imageOffset.y };
              }
            }}
            onMouseMove={(e) => {
              if (panStartRef.current && imageZoom > 1) {
                setImageOffset({
                  x: e.clientX - panStartRef.current.x,
                  y: e.clientY - panStartRef.current.y,
                });
              }
            }}
            onMouseUp={() => {
              panStartRef.current = null;
            }}
            onMouseLeave={() => {
              panStartRef.current = null;
            }}
            onTouchStart={(e) => {
              if (e.touches.length === 1 && imageZoom === 1 && currentImageIndex >= 0) {
                // Single touch for swipe
                const touch = e.touches[0];
                panStartRef.current = { x: touch.clientX, y: touch.clientY };
              }
            }}
            onTouchMove={(e) => {
              if (e.touches.length === 1 && imageZoom === 1 && panStartRef.current && currentImageIndex >= 0) {
                const touch = e.touches[0];
                const deltaX = touch.clientX - panStartRef.current.x;
                // Swipe threshold: 50px
                if (Math.abs(deltaX) > 50) {
                  if (deltaX > 0 && currentImageIndex > 0) {
                    // Swipe right - previous image
                    const newIndex = currentImageIndex - 1;
                    setCurrentImageIndex(newIndex);
                    setFullScreenImage(mediaItems[newIndex] ? mediaUrls[mediaItems[newIndex].path] || null : null);
                    panStartRef.current = null;
                  } else if (deltaX < 0 && currentImageIndex < mediaItems.length - 1) {
                    // Swipe left - next image
                    const newIndex = currentImageIndex + 1;
                    setCurrentImageIndex(newIndex);
                    setFullScreenImage(mediaItems[newIndex] ? mediaUrls[mediaItems[newIndex].path] || null : null);
                    panStartRef.current = null;
                  }
                }
              }
            }}
            onTouchEnd={() => {
              panStartRef.current = null;
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fullScreenImage}
              alt="Full screen"
              className="max-w-full max-h-full object-contain select-none"
              style={{
                transform: imageZoom > 1 
                  ? `translate(${imageOffset.x}px, ${imageOffset.y}px) scale(${imageZoom})`
                  : 'scale(1)',
                transformOrigin: 'center center',
                cursor: imageZoom > 1 ? 'grab' : 'default',
                transition: imageZoom === 1 ? 'transform 0.2s' : 'none',
              }}
              draggable={false}
            />
          </div>

          {/* Zoom Indicator */}
          {imageZoom > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs bg-black/50 px-3 py-1 rounded-full z-10">
              {Math.round(imageZoom * 100)}%
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirmDelete') || 'Confirm Delete'}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('common.deleteServiceEntryConfirm') || 'Are you sure you want to permanently delete this service entry? This action cannot be undone.'}
          </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-row justify-between items-center">
            <AlertDialogCancel>{t('actions.cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600">
              {t('actions.delete') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default ServiceEntryViewPage;

